import type {
  EventOrderNotificationSettings,
  EventOrderFulfillmentStatus,
  EventOrderPaymentStatus,
  EventOrderRecord,
} from '../types';
import { getSiteAuthRuntime, getStoredToken } from './bliss-app-auth';

function getErrorMessage(payload: unknown, fallback: string): string {
  if (!payload || typeof payload !== 'object') {
    return fallback;
  }

  const source = payload as {
    error?: string | { message?: string };
    message?: string;
    data?: { message?: string };
  };

  if (typeof source.error === 'string' && source.error) {
    return source.error;
  }

  if (source.error && typeof source.error === 'object' && typeof source.error.message === 'string') {
    return source.error.message;
  }

  if (typeof source.message === 'string' && source.message) {
    return source.message;
  }

  if (source.data && typeof source.data.message === 'string' && source.data.message) {
    return source.data.message;
  }

  return fallback;
}

async function readResponseJson(response: Response): Promise<unknown> {
  return response.json().catch(() => null);
}

async function requestEventOrdersApi<T>(path: string, options: RequestInit = {}): Promise<T> {
  const runtime = await getSiteAuthRuntime();
  const token = getStoredToken();
  const headers = new Headers(options.headers);
  const method = (options.method || 'GET').toUpperCase();
  const hasBody = options.body != null;

  if (!token) {
    throw new Error('You need to sign in again before reviewing event orders.');
  }

  if (hasBody && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (!headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(`${runtime.apiBaseUrl}${path}`, {
    ...options,
    method,
    headers,
  });

  const payload = await readResponseJson(response);
  if (!response.ok) {
    throw new Error(getErrorMessage(payload, 'The event orders workspace could not reach the Bliss app API.'));
  }

  const source = payload as { data?: T };
  return (source.data ?? payload) as T;
}

export interface ListEventOrdersOptions {
  paymentStatus?: EventOrderPaymentStatus | 'all';
  fulfillmentStatus?: EventOrderFulfillmentStatus | 'all';
  limit?: number;
}

export async function listEventOrders(options: ListEventOrdersOptions = {}): Promise<EventOrderRecord[]> {
  const searchParams = new URLSearchParams();

  if (options.paymentStatus && options.paymentStatus !== 'all') {
    searchParams.set('payment_status', options.paymentStatus);
  }

  if (options.fulfillmentStatus && options.fulfillmentStatus !== 'all') {
    searchParams.set('fulfillment_status', options.fulfillmentStatus);
  }

  if (typeof options.limit === 'number' && Number.isFinite(options.limit)) {
    searchParams.set('limit', String(options.limit));
  }

  const query = searchParams.toString();
  const result = await requestEventOrdersApi<{ orders: EventOrderRecord[] }>(
    `/events/orders${query ? `?${query}` : ''}`,
  );

  return Array.isArray(result.orders) ? result.orders : [];
}

export async function updateEventOrderFulfillmentStatus(
  orderId: string,
  fulfillmentStatus: EventOrderFulfillmentStatus,
): Promise<EventOrderRecord> {
  const result = await requestEventOrdersApi<{ order: EventOrderRecord }>(
    `/events/orders/${encodeURIComponent(orderId)}`,
    {
      method: 'PUT',
      body: JSON.stringify({
        fulfillment_status: fulfillmentStatus,
      }),
    },
  );

  return result.order;
}

export async function refundEventOrder(orderId: string): Promise<EventOrderRecord> {
  const result = await requestEventOrdersApi<{ order: EventOrderRecord }>(
    `/events/orders/${encodeURIComponent(orderId)}/refund`,
    {
      method: 'POST',
      body: JSON.stringify({}),
    },
  );

  return result.order;
}

export async function sendEventOrderReceipt(orderId: string): Promise<{
  order_id: string;
  buyer: {
    recipient_email: string;
    sent: boolean;
    skipped: 'already_sent' | 'missing_recipient' | null;
  };
  owner: {
    sent: boolean;
    skipped: 'already_sent' | 'missing_recipient' | null;
  };
}> {
  return requestEventOrdersApi(`/events/orders/${encodeURIComponent(orderId)}/receipt`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function getEventOrderNotificationRecipient(): Promise<EventOrderNotificationSettings> {
  return requestEventOrdersApi<EventOrderNotificationSettings>('/events/orders/notification-recipient');
}

export async function updateEventOrderNotificationRecipient(input: {
  recipientEmail: string;
}): Promise<EventOrderNotificationSettings> {
  return requestEventOrdersApi<EventOrderNotificationSettings>('/events/orders/notification-recipient', {
    method: 'PUT',
    body: JSON.stringify({
      recipient_email: input.recipientEmail.trim(),
    }),
  });
}
